import React from 'react';
import Helmet from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import classNames from 'classnames';
import SVG from 'react-inlinesvg';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import companyData from '../data/company';

import '../styles/index.scss';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Popups from './Popups';
import Contact from './sections/Contact';

import PatternDotsSrc from '../img/svg/pattern-dots.svg';
import PatternDotsDenseSrc from '../img/svg/pattern-dots-dense.svg';

const getMeta = (value, data, meta) => {
  let ifMetaExist =
    `${value}` in meta && meta[`${value}`]
    !== null && (meta[`${value}`].length > 1 || meta[`${value}`] === true || false);

  return ifMetaExist ? meta[`${value}`] : data.site.siteMetadata[`${value}`];
}

const getMetaTitle = (data, meta) => {
  let url = '';
  if (meta.title && meta.title.length > 1) {
    // for root page
    if (typeof window !== 'undefined' && window.location && window.location.pathname == "/") {
      url = meta.title;
    } else {
      url = meta.title + data.site.siteMetadata['titlePostfix'];
    }
  } else {
    url = data.site.siteMetadata['title'];
  }

  return url
}

const getMetaImage = (data, meta) => {
  let url = (meta.image && meta.image.publicURL) ?
    (meta.image && meta.image.publicURL)
    : data.site.siteMetadata['image'];

  return data.site.siteMetadata.siteUrl + url
}

const TemplateWrapper = ({
  children,
  meta = {},
  variants = [],
  showNavbar = true,
  showContact = true,
  contactTitle = '',
  contactText = '',
  showNewsletter = true,
  showFooter = true,
  contactSuccessCb = null,
  dataPage = null,
}) => {
  if (companyData.banner.visible) {
    variants.push('banner');
  }
  const variantClasses = variants.map(variant => `Layout--${variant}`);
  const classes = classNames('Layout', variantClasses);

  const data = useStaticQuery(graphql`
        query HeadingQuery {
          site {
            siteMetadata {
              title
              titlePostfix
              description
              image
              siteUrl
              menuItems {
                label
                to
                dropdown {
                  label
                  to
                }
              }
              reCaptchaKey
            }
          }
        }
      `);

  return (
    <>
      <GoogleReCaptchaProvider
        reCaptchaKey={data.site.siteMetadata.reCaptchaKey}
        scriptProps={{
          async: false, // optional, default to false,
          defer: false, // optional, default to false
          appendTo: 'head', // optional, default to "head", can be "head" or "body",
          nonce: undefined // optional, default undefined
        }}
      >
        <main className={classes}>
          <Helmet>
            <html lang="en" />
            <title>{getMetaTitle(data, meta)}</title>
            <meta name="description" content={getMeta('description', data, meta)} />

            {(typeof window !== 'undefined' && window.location) && (
              <link rel="canonical" href={(data.site.siteMetadata.siteUrl + window.location.pathname).replace(/\/?$/, '/')} />
            )}

            {getMeta('hide_robot', data, meta) && (
              <meta name="robots" content="noindex, nofollow" />
            )}

            <meta name="google-site-verification" content="MvMgafsMi4j3VDvP0iDnf1ZAMwDOgmOI7DPh0rFJt1M" />

            <link
              rel="getindata-big-data-trainings-platforms"
              sizes="180x180"
              href="/img/getindata-big-data-trainings-platforms.png"
            />
            <link
              rel="icon"
              type="image/png"
              href="/img/favicon-32x32.png"
              sizes="32x32"
            />
            <link
              rel="icon"
              type="image/png"
              href="/img/favicon-16x16.png"
              sizes="16x16"
            />

            <link
              rel="mask-icon"
              href="/img/safari-pinned-tab.svg"
              color="#ff4400"
            />
            <meta name="theme-color" content="#fff" />

            <meta name="twitter:title" content={getMeta('title', data, meta)} />
            <meta name="twitter:description" content={getMeta('description', data, meta)} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={data.site.siteMetadata.siteUrl + (meta.image && meta.image.publicURL)} />

            <meta property="og:title" content={getMeta('title', data, meta)} />
            <meta property="og:image" content={getMetaImage(data, meta)} />
            <meta property="og:description" content={getMeta('description', data, meta)} />
            {(typeof window !== 'undefined' && window.location) && (
              <meta property="og:url" content={window.location.href} />
            )}
          </Helmet>

          {showNavbar && <Navbar items={data.site.siteMetadata.menuItems} banner={companyData.banner} />}

          {variants.includes('backgrounded') && (
            <>
              <SVG
                src={PatternDotsSrc}
                uniquifyIDs={false}
                className="Layout__pattern"
              />
              <SVG
                src={PatternDotsDenseSrc}
                uniquifyIDs={false}
                className="Layout__pattern"
              />

              {/* <div className="Layout__background">
                <SVG src={ImageStarsSrc} className="Layout__stars" />
              </div> */}
            </>
          )}

          <>{children}</>

          {showContact && <Contact title={contactTitle} text={contactText} contactSuccessCb={contactSuccessCb} />}

          {showFooter && <Footer menuItems={data.site.siteMetadata.menuItems} />}

          <Popups data={dataPage} />
        </main>
      </GoogleReCaptchaProvider>
    </>
  );
};

export default TemplateWrapper;
